import React from 'react';
import PropTypes from 'prop-types';
import Grid from 'src/ui/library/Grid/Grid';
import TextInput from 'src/ui/library/TextInput';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import DropdownNew from 'src/ui/library/DropdownNew';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import intl from 'react-intl-universal';
import ServiceTitlePres from './ServiceTitlePres';
import { SI_ADD_APPLICANT_VIEWS } from 'src/constants/constants';
import DateInput from 'src/ui/library/DateInput';
import PhoneInput from 'src/ui/library/PhoneInput';

export default function AddApplicantPres({
  contactNumber = '',
  changeEmployeeView,
  firstName = '',
  lastName = '',
  dateOfBirth = '',
  error = null,
  emailAddress = '',
  passportCountry = '',
  passportCountryList = [],
  relationshipStatus = '',
  relationshipStatusList = [],
  employee = null,
  employeeCode = '',
  setValue,
  addApplicantViewType = SI_ADD_APPLICANT_VIEWS.SEARCH,
  onEmployeeSelect,
  onSearch,
}) {
  const isSearchView = addApplicantViewType === SI_ADD_APPLICANT_VIEWS.SEARCH;
  const isAddNewView = addApplicantViewType === SI_ADD_APPLICANT_VIEWS.ADDNEW;
  const [employeeOptions, setEmployeeOptions] = React.useState([]);
  const [isLoadingEmployees, setIsLoadingEmployees] = React.useState(false);
  const [employeeSearchText, setEmployeeSearchText] = React.useState('');
  const [hasSearchedEmployees, setHasSearchedEmployees] = React.useState(false);
  const employeeSearchRequestId = React.useRef(0);

  const onEmployeeInputChange = (event, inputValue, reason) => {
    const requestId = employeeSearchRequestId.current + 1;
    employeeSearchRequestId.current = requestId;
    setEmployeeSearchText(inputValue);

    if (reason === 'reset') {
      return;
    }

    if (!inputValue) {
      setEmployeeOptions([]);
      setHasSearchedEmployees(false);
      setIsLoadingEmployees(false);
      return;
    }

    setIsLoadingEmployees(true);
    setHasSearchedEmployees(false);
    Promise.resolve(onSearch(inputValue))
      .then(result => {
        if (employeeSearchRequestId.current === requestId) {
          setEmployeeOptions((result && result.options) || []);
        }
      })
      .catch(() => {
        if (employeeSearchRequestId.current === requestId) {
          setEmployeeOptions([]);
        }
      })
      .finally(() => {
        if (employeeSearchRequestId.current === requestId) {
          setHasSearchedEmployees(true);
          setIsLoadingEmployees(false);
        }
      });
  };

  return (
    <section className={'ssi-addapplicant'}>
      <ServiceTitlePres title={intl.get('serviceInit.addInit.applicant.title')} />

      {isSearchView && (
        <React.Fragment>
          <div className={'ssi-addapplicant-search'}>
            <Autocomplete
              options={employeeOptions}
              value={employee && employee.employeeId ? employee : null}
              inputValue={employeeSearchText}
              loading={isLoadingEmployees}
              filterOptions={(options, state) =>
                options.filter(option => {
                  const searchText = state.inputValue.toLowerCase();
                  const employeeName = option.employeeName ? option.employeeName.toLowerCase() : '';
                  const email = option.email ? option.email.toLowerCase() : '';

                  return employeeName.indexOf(searchText) >= 0 || email.indexOf(searchText) >= 0;
                })
              }
              getOptionLabel={option => option.employeeName || ''}
              isOptionEqualToValue={(option, value) => option.employeeId === value.employeeId}
              noOptionsText={employeeSearchText && hasSearchedEmployees ? intl.get('serviceInit.addInit.applicant.noResults') : 'Type to search'}
              onInputChange={onEmployeeInputChange}
              onChange={(event, value) => onEmployeeSelect(value)}
              renderInput={params => (
                <TextField
                  {...params}
                  size="small"
                  placeholder={intl.get('serviceInit.addInit.applicant.search')}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {isLoadingEmployees ? <CircularProgress color="inherit" size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                />
              )}
            />
          </div>
          <div className={'addlink-footer'}>
            <a onClick={changeEmployeeView.bind(null, 'open')}>+ {intl.get('serviceInit.addInit.applicant.addEmployee')}</a>
          </div>
        </React.Fragment>
      )}

      {isAddNewView && (
        <div className={'ssi-addapplicant-new'}>
          <a className={'ssi-nextreturn-section'} onClick={changeEmployeeView.bind(null, 'close')}>
            <ArrowBackIcon />
            <span>{intl.get('serviceInit.addInit.applicant.return')}</span>
          </a>

          <Grid container spacing={0} className={'ssi-addapplicant-container'}>
            {error && (
              <Grid item xs={12} className={'ssi-addapplicant-grid error'}>
                {intl.get('serviceInit.addInit.applicant.error')}: {error.message}
              </Grid>
            )}
            <Grid item xs={12} className={'ssi-addapplicant-grid'}>
              <TextInput
                className={'ssi-addapplicant-griditem'}
                label={intl.get('serviceInit.addInit.applicant.firstName')}
                required={true}
                value={firstName}
                onChange={event => setValue('firstName', event.target.value)}
                id="firstName"
              />
              <TextInput
                className={'ssi-addapplicant-griditem'}
                label={intl.get('serviceInit.addInit.applicant.lastName')}
                required={true}
                value={lastName}
                onChange={event => setValue('lastName', event.target.value)}
                id="lastName"
              />
              <TextInput
                className={'ssi-addapplicant-griditem'}
                label={intl.get('serviceInit.addInit.applicant.email')}
                required={true}
                value={emailAddress}
                onChange={event => setValue('emailAddress', event.target.value)}
                id="emailAddress"
              />
            </Grid>
            <Grid item xs={12} className={'ssi-addapplicant-grid'}>
              <DateInput
                className={'ssi-addapplicant-griditem'}
                styleType={'serviceInitation'}
                value={dateOfBirth}
                required={true}
                minDate={1930}
                maxDate={new Date().getFullYear()}
                id={`dateOfBirth`}
                name="dateOfBirth"
                label={intl.get('serviceInit.addInit.applicant.dateOfBirth')}
                format="DD-MMM-YYYY"
                inputMode="editable"
                onChange={date => setValue('dateOfBirth', date)}
              />
              <DropdownNew
                id="passportCountry"
                name="passportCountry"
                label={intl.get('serviceInit.addInit.applicant.passportCountry')}
                options={
                  passportCountryList && passportCountryList.map(item => ({ value: item.countryName, label: item.countryName }))
                }
                className={'ssi-addapplicant-griditem ssi-dropdown'}
                disableUnderline={true}
                required={true}
                value={passportCountry}
                onChange={event => setValue('passportCountry', event.target.value)}
              />

              <PhoneInput
                className={'ssi-addapplicant-griditem'}
                label={intl.get('serviceInit.addInit.applicant.contactNumber')}
                required={true}
                value={contactNumber}
                onChange={event => setValue('contactNumber', event.target.value)}
                id="contactNumber"
              />
            </Grid>
            <Grid item xs={12} className={'ssi-addapplicant-grid'}>
              <DropdownNew
                id="relationshipStatus"
                name="relationshipStatus"
                label={intl.get('serviceInit.addInit.applicant.relationshipStatus')}
                options={
                  relationshipStatusList && relationshipStatusList.map(item => ({ value: item.status, label: item.status }))
                }
                className={'ssi-addapplicant-griditem ssi-dropdown'}
                disableUnderline={true}
                required={true}
                value={relationshipStatus}
                onChange={event => setValue('relationshipStatus', event.target.value)}
              />
              <TextInput
                className={'ssi-addapplicant-griditem'}
                label={intl.get('serviceInit.addInit.applicant.employeeCode')}
                required={true}
                value={employeeCode}
                onChange={event => setValue('employeeCode', event.target.value)}
                id="employeeCode"
              />
            </Grid>
          </Grid>
        </div>
      )}
    </section>
  );
}

AddApplicantPres.propTypes = {
  contactNumber: PropTypes.string,
  changeEmployeeView: PropTypes.func.isRequired,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  dateOfBirth: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  error: PropTypes.shape({
    message: PropTypes.string,
  }),
  emailAddress: PropTypes.string,
  passportCountry: PropTypes.string,
  passportCountryList: PropTypes.arrayOf(
    PropTypes.shape({
      countryName: PropTypes.string,
    }),
  ),
  relationshipStatus: PropTypes.string,
  relationshipStatusList: PropTypes.arrayOf(
    PropTypes.shape({
      status: PropTypes.string,
    }),
  ),
  employee: PropTypes.shape({
    employeeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    employeeName: PropTypes.string,
    email: PropTypes.string,
  }),
  employeeCode: PropTypes.string,
  setValue: PropTypes.func.isRequired,
  addApplicantViewType: PropTypes.string,
  onEmployeeSelect: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
};

